// src/utils/qr-batch-print.js
import { jsPDF } from "jspdf";
import QRCode from "qrcode";

// =====================================================
// Batch QR printing for Brother PT-P750W (cinta 18mm)
// - Usamos el MISMO tamaño que el QR individual: 20 x 18 mm
// - Generamos el QR con canvas (más estable que QRCode.toDataURL en batch)
// - Siempre dibujamos marco + textos para evitar PDF "en blanco"
// =====================================================

const LABEL_W_MM = 20; // largo (feed)
const LABEL_H_MM = 18; // ancho cinta (alto)
const MARGIN_MM = 2;

const QR_SIZE_MM = 9;

// --- Genera PNG dataURL de forma estable usando canvas ---
async function qrPngDataUrl(payload) {
  const text = String(payload ?? "").trim();
  if (!text) throw new Error("Payload vacío para QR.");

  const canvas = document.createElement("canvas");
  await QRCode.toCanvas(canvas, text, {
    errorCorrectionLevel: "H",
    margin: 1,
    scale: 8,
    color: { dark: "#000000", light: "#FFFFFF" },
  });

  return canvas.toDataURL("image/png");
}

function drawOneLabel(doc, payload, dataUrl) {
  const qrX = (LABEL_W_MM - QR_SIZE_MM) / 2;
  const qrY = MARGIN_MM;

  // QR (si falla, dejamos placeholder visible)
  if (dataUrl) {
    doc.addImage(
      dataUrl,
      "PNG",
      qrX,
      qrY,
      QR_SIZE_MM,
      QR_SIZE_MM,
      undefined,
      "FAST"
    );
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("QR ERROR", LABEL_W_MM / 2, qrY + QR_SIZE_MM / 2, {
      align: "center",
    });
  }

  // Textos (siempre visibles)
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);

  const idY = qrY + QR_SIZE_MM + 3.0;
  const calinsY = idY + 3.2;

  doc.text(`ID: ${payload}`, LABEL_W_MM / 2, idY, { align: "center" });
  doc.text("Calins", LABEL_W_MM / 2, calinsY, { align: "center" });
}

/**
 * buildBatchQrPdfPages
 * Genera un PDF multi-página (20x18mm por página).
 * payloads: ["12003","0221-1391", ...]
 */
export async function buildBatchQrPdfPages({
  payloads,
  fileName = "QR_OS.pdf",
} = {}) {
  const clean = (payloads || [])
    .map((p) => String(p ?? "").trim())
    .filter(Boolean);

  if (clean.length === 0) {
    throw new Error("No hay instrumentos/payloads para imprimir.");
  }

  const doc = new jsPDF({
    unit: "mm",
    format: [LABEL_W_MM, LABEL_H_MM],
    compress: false, // evita ciertos casos raros de render en algunos viewers
  });

  let first = true;

  for (const payload of clean) {
    if (!first) doc.addPage([LABEL_W_MM, LABEL_H_MM], "portrait");
    first = false;

    let dataUrl = null;
    try {
      dataUrl = await qrPngDataUrl(payload);
    } catch (e) {
      console.error("[qr-batch-print] Error generando QR:", payload, e);
    }

    drawOneLabel(doc, payload, dataUrl);
  }

  const blob = doc.output("blob");

  // Diagnóstico útil: si es demasiado chico, suele ser vacío o falló el render
  if (blob.size < 1200) {
    console.warn("[qr-batch-print] Blob sospechosamente chico:", blob.size);
  }

  return { blob, fileName };
}

/**
 * printPdfBlobInHiddenIframe
 * Imprime el blob SIN abrir popups (más estable en Chrome/Windows).
 */
export function printPdfBlobInHiddenIframe(blob) {
  const url = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;

  document.body.appendChild(iframe);

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch {}
        URL.revokeObjectURL(url);
      }, 4000);
    }
  };
}

// ------------------------------------------------------------------
// Compat: si en algún lugar todavía importás funciones viejas,
// las dejamos como wrappers para no romper.
// ------------------------------------------------------------------

/** @deprecated Usá buildBatchQrPdfPages */
export async function buildBatchQrStripPdf(opts = {}) {
  return buildBatchQrPdfPages(opts);
}

/** @deprecated Usá printPdfBlobInHiddenIframe */
export function openPrintBlob(blob) {
  return printPdfBlobInHiddenIframe(blob);
}
